/**
 * Order map — logistics-style tracking dashboard (orders list + live map).
 */

import React, { useContext, useEffect, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import moment from 'moment-timezone';
import { useNavigate } from 'react-router-dom';
import { ORDER_TYPE_RESTAURANT } from './orderManagementConfig';
import { OrderTrackingDashboard } from './order-tracking-map/OrderTrackingDashboard';
import { useOrderManagementOrders } from './useOrderManagementOrders';
import { GlobalStateDataContext } from '../../context-api/data-provider/GlobalStateDataProvider';
import { localStorageKeys } from '../../utils/constants';
import './OrderManagementMap.scss';

const formattedDateTime = () => {
  return moment().format('MM/DD/YYYY - hh:mm A');
};

const OrderManagementMap = () => {
  const socketRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const {
    onLoadRegBranchResponse,
    setOrderFromSocket,
    branchSessionLogout,
    setStatsDataFromSocket,
  } = useContext(GlobalStateDataContext);
  const { TOKEN } = localStorageKeys;
  const [connectionStatus, setConnectionStatus] = useState('Disconnected');
  const [notificationServerDown, setNotificationServerDown] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  const URL_PATH_NOTIFICATION_SERVER = process.env.REACT_APP_NOTIFICATION_SERVER_URL;
  const RegionalBranchName =
    onLoadRegBranchResponse?.data?.data?.data?.RegionalBranchInfo?.branchName;
  const navigate = useNavigate();
  const orders = useOrderManagementOrders();

  useEffect(() => {
    orders.setOrderTypeView(ORDER_TYPE_RESTAURANT);
  }, [orders.setOrderTypeView]);

  useEffect(() => {
    const RELOAD_LOCK_KEY = 'regional_socket_reloaded_once';
    let reloadTimer = null;
    let isUnmounting = false;

    const socket = io(URL_PATH_NOTIFICATION_SERVER, {
      reconnection: true,
      reconnectionAttempts: Infinity,
      forceNew: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 2000,
      timeout: 5000,
      query: {
        type: 'regional_frontend_app',
      },
      transports: ['websocket'],
      auth: {
        token: localStorage.getItem(TOKEN),
      },
    });

    socketRef.current = socket;

    const reloadOnceAfterDisconnect = () => {
      if (isUnmounting) return;

      const alreadyReloaded = sessionStorage.getItem(RELOAD_LOCK_KEY);

      if (alreadyReloaded) return;

      sessionStorage.setItem(RELOAD_LOCK_KEY, 'true');

      reloadTimer = setTimeout(() => {
        window.location.reload();
      }, 1000);
    };

    const isPromoOrderNo = (orderNo) =>
      typeof orderNo === 'string' && orderNo.toUpperCase().includes('-PROMO');

    const handleRegionalStats = (arg, callback) => {
      try {
        console.log(`📶 Order from Notification Server ${formattedDateTime()}`, arg);

        setLastUpdated(formattedDateTime());

        if (arg?.order_no) {
          setOrderFromSocket((prev) => {
            const index = prev.findIndex((order) => order.order_no === arg.order_no);

            if (index !== -1) {
              const updated = [...prev];
              updated[index] = arg;
              return updated;
            }

            return [...prev, arg];
          });
        }

        if (arg?.order_no || arg?.id || arg?._id) {
          if (isPromoOrderNo(arg?.order_no)) return;

          const socketId = arg?.id ?? arg?._id;
          orders.setAllOrders((prev) => {
            const index = prev.findIndex(
              (order) =>
                (arg?.order_no && order.order_no === arg.order_no) ||
                (socketId && (order._id === socketId || order.id === socketId))
            );

            if (index === -1) return [...prev, arg];

            const updated = [...prev];
            updated[index] = { ...updated[index], ...arg };
            return updated;
          });
        }

        if (typeof callback === 'function') {
          callback({
            received: true,
            regionalBranchName: RegionalBranchName,
            receivedAt: new Date().toISOString(),
            message: 'Regional frontend received stats successfully',
          });
        }
      } catch (error) {
        console.error('Error processing regional stats:', error);

        if (typeof callback === 'function') {
          callback({
            received: false,
            regionalBranchName: RegionalBranchName,
            error: error.message,
            receivedAt: new Date().toISOString(),
            message: 'Regional frontend failed to process stats',
          });
        }
      }
    };

    const onConnect = () => {
      reconnectAttemptsRef.current = 0;
      setConnectionStatus('Connected');
      setNotificationServerDown(false);
      console.log(`Connected ${socket.id} ${formattedDateTime()}`);
    };

    const onDisconnect = (reason) => {
      setConnectionStatus('Disconnected: ' + reason);
      setNotificationServerDown(true);

      console.log(`Disconnected ${reason} ${formattedDateTime()}`);

      reloadOnceAfterDisconnect();
    };

    const onReconnectAttempt = (attemptNumber) => {
      reconnectAttemptsRef.current = attemptNumber;
      setConnectionStatus(`Reconnecting... Attempt ${attemptNumber}`);
      console.log(`Reconnection attempt #${attemptNumber}`);
    };

    const onReconnectFailed = () => {
      setConnectionStatus('Reconnection failed');
      setNotificationServerDown(true);
      console.error('Notification server failed to reconnect');
    };

    const onReconnect = () => {
      setNotificationServerDown(false);
      setConnectionStatus('Reconnected successfully');
      console.log('Notification server reconnected successfully');
    };

    const onConnectError = (e) => {
      console.log(`Socket Server is down ${e.message} ${formattedDateTime()}`);
      setNotificationServerDown(true);
    };

    const onSocketAuthError = (data) => {
      if (data?.message === 'Invalid token') {
        branchSessionLogout(navigate);
        setNotificationServerDown(true);
      }
    };

    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);
    socket.on('reconnect_attempt', onReconnectAttempt);
    socket.on('reconnect_failed', onReconnectFailed);
    socket.on('reconnect', onReconnect);
    socket.on('connect_error', onConnectError);
    socket.on('socket-auth-error', onSocketAuthError);

    const pingInterval = setInterval(() => {
      if (socket.connected) {
        socket.emit('ping-from-regionalbranch', {}, (ack) => {
          setStatsDataFromSocket(ack?.stats);
        });

        console.log(`📡 Sent ping from OrderManagementMap ${formattedDateTime()}`);
      }
    }, 60000);

    socket.on(`pong-${RegionalBranchName}`, handleRegionalStats);
    socket.on(RegionalBranchName, handleRegionalStats);

    return () => {
      isUnmounting = true;

      if (reloadTimer) clearTimeout(reloadTimer);
      clearInterval(pingInterval);

      socket.off(`pong-${RegionalBranchName}`, handleRegionalStats);
      socket.off(RegionalBranchName, handleRegionalStats);
      socket.off('connect', onConnect);
      socket.off('disconnect', onDisconnect);
      socket.off('reconnect_attempt', onReconnectAttempt);
      socket.off('reconnect_failed', onReconnectFailed);
      socket.off('reconnect', onReconnect);
      socket.off('connect_error', onConnectError);
      socket.off('socket-auth-error', onSocketAuthError);
      socket.removeAllListeners();
      socket.disconnect();
      socketRef.current = null;
    };
  }, [RegionalBranchName]);

  return (
    <div className='order-map-page'>
      <div className='order-map-page__content'>
        <OrderTrackingDashboard orders={orders} />
      </div>
    </div>
  );
};

export default OrderManagementMap;
